<template>
  <div class="max-w-4xl mx-auto bg-white p-6 md:p-10 rounded-xl shadow-lg border border-gray-100 space-y-8">
    <div class="border-b pb-4">
      <h2 class="text-3xl font-bold text-gray-800">
        Create Artist Profile
      </h2>
      <p class="text-gray-500 mt-1 text-sm">Fill in your details to showcase your work.</p>
    </div>

    <!-- Artist Details -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="space-y-2">
        <label class="block text-sm font-semibold text-gray-700">Artist Name</label>
        <input v-model="form.name" type="text" placeholder="e.g. John Doe" class="input-field" />
      </div>

      <div class="space-y-2">
        <label class="block text-sm font-semibold text-gray-700">Profession/Designation</label>
        <select v-model="form.profession" class="input-field">
          <option value="" disabled>Select Profession</option>
          <option v-for="prof in professions" :key="prof" :value="prof">
            {{ prof }}
          </option>
        </select>
      </div>
    </div>

    <!-- DOB and Quote -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="space-y-2">
        <label class="block text-sm font-semibold text-gray-700">Date of Birth</label>
        <input v-model="form.dob" type="date" class="input-field" />
      </div>

      <div class="space-y-2">
        <label class="block text-sm font-semibold text-gray-700">Favorite Quote</label>
        <input v-model="form.quote" type="text" placeholder="e.g. Art is life..." class="input-field" />
      </div>
    </div>

    <div class="space-y-2">
      <label class="block text-sm font-semibold text-gray-700">Description</label>
      <textarea v-model="form.description" rows="4" placeholder="Tell us about yourself..." class="input-field resize-y"></textarea>
    </div>

    <div class="space-y-2">
      <label class="block text-sm font-semibold text-gray-700">Achievements</label>
      <textarea v-model="form.achievements" rows="3" placeholder="List your key achievements..." class="input-field resize-y"></textarea>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Profile Image -->
      <div class="space-y-2">
        <label class="block text-sm font-semibold text-gray-700">Profile Image</label>
        <div class="relative border-2 border-dashed border-gray-300 rounded-lg p-6 hover:bg-gray-50 transition text-center cursor-pointer group">
          <input type="file" @change="handleProfileImage" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept="image/*" />
          <div v-if="!form.image" class="space-y-1">
            <span class="block text-gray-400 group-hover:text-gray-600 transition">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span class="text-sm font-medium text-gray-600">Click to upload profile image</span>
            </span>
          </div>
          <div v-else class="flex items-center justify-center gap-2">
            <span class="text-green-600 font-medium text-sm truncate max-w-[200px]">{{ form.image.name }}</span>
            <button @click.stop="form.image = null" class="text-gray-400 hover:text-red-500">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Gallery Images -->
      <div class="space-y-2">
        <label class="block text-sm font-semibold text-gray-700">Gallery Images (Max 3)</label>
        <div class="relative border-2 border-dashed border-gray-300 rounded-lg p-6 hover:bg-gray-50 transition text-center cursor-pointer group">
          <input type="file" multiple @change="handleGalleryImages" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept="image/*" />
          <div v-if="galleryImages.length === 0" class="space-y-1">
            <span class="block text-gray-400 group-hover:text-gray-600 transition">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              <span class="text-sm font-medium text-gray-600">Upload up to 3 images</span>
            </span>
          </div>
          <div v-else class="space-y-2">
            <div v-for="img in galleryImages" :key="img.name" class="flex items-center justify-between bg-gray-100 px-3 py-1 rounded text-sm">
              <span class="truncate max-w-[150px] text-gray-700">{{ img.name }}</span>
            </div>
            <p v-if="galleryImages.length > 0" class="text-xs text-gray-500 mt-1">Click to change selection</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Artist Links -->
    <div class="bg-gray-50 p-6 rounded-xl border border-gray-200">
      <div class="flex justify-between items-center mb-4">
        <h3 class="font-bold text-gray-800">Artist Links</h3>
        <button @click="addLink" class="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded-full font-semibold hover:bg-blue-100 transition flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Link
        </button>
      </div>

      <div class="space-y-3">
        <div v-for="(link, index) in links" :key="index" class="flex flex-col md:flex-row gap-3 items-start md:items-center bg-white p-3 rounded-lg border border-gray-200 shadow-sm animate-fade-in">
          <div class="flex-1 w-full md:w-auto">
             <input v-model="link.link_type" placeholder="Platform (e.g. Instagram)" class="input-field text-sm" />
          </div>
          <div class="flex-[2] w-full md:w-auto">
             <input v-model="link.link_url" placeholder="URL (e.g. https://instagram.com/...)" class="input-field text-sm" />
          </div>
          <button @click="removeLink(index)" class="text-gray-400 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition self-end md:self-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <p v-if="links.length === 0" class="text-sm text-gray-500 text-center py-2">No links added yet.</p>
      </div>
    </div>

    <!-- Submit -->
    <div class="flex flex-col-reverse md:flex-row gap-4 pt-4 border-t border-gray-100">
      <button type="button" @click="$emit('cancel')" class="w-full md:w-auto border border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition">
        Cancel
      </button>
      <button type="button" @click="submitForm" class="w-full md:w-auto bg-[#BF3131] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#a62b2b] shadow-md hover:shadow-lg transition">
        Save Profile
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from 'sweetalert2';

export default {
  name: "ArtistCreateForm",
  data() {
    return {
      form: {
        name: "",
        profession: "",
        description: "",
        achievements: "",
        dob: "",
        quote: "",
        image: null,
      },
      galleryImages: [],
      links: [{ link_type: "", link_url: "" }],
      userId: null,
      professions: [
        "Artist",
        "Singer",
        "Actor",
        "Actress",
        "Socialist",
        "Musician",
        "Dancer",
        "Writer",
        "Painter",
        "Photographer",
        "Other"
      ]
    };
  },
  mounted() {
    // Get user ID from localStorage
    const userString = localStorage.getItem("user");
    if (userString) {
      try {
        const user = JSON.parse(userString);
        this.userId = user.id;
        console.log("✅ User ID loaded:", this.userId);
      } catch (e) {
        console.error("❌ Error parsing user data:", e);
      }
    }
  },
  methods: {
    async handleProfileImage(e) {
      if (e.target.files.length > 0) {
        try {
          const file = e.target.files[0];
          this.form.image = await this.compressImage(file);
        } catch (error) {
          console.error("Image compression failed:", error);
          this.form.image = e.target.files[0]; // Fallback
        }
      }
    },
    async handleGalleryImages(e) {
      const files = Array.from(e.target.files);
      const processedFiles = [];
      
      for (const file of files) {
        try {
          const compressed = await this.compressImage(file);
          processedFiles.push(compressed);
        } catch (error) {
          console.error("Gallery image compression failed:", error);
          processedFiles.push(file); // Fallback
        }
      }
      
      // Append new files instead of replacing, up to 3 total
      const totalImages = [...this.galleryImages, ...processedFiles].slice(0, 3);
      this.galleryImages = totalImages;
    },
    compressImage(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 1200;
            const MAX_HEIGHT = 1200;
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Canvas is empty'));
                return;
              }
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            }, 'image/jpeg', 0.8);
          };
          img.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
      });
    },
    addLink() {
      this.links.push({ link_type: "", link_url: "" });
    },
    removeLink(index) {
      this.links.splice(index, 1);
    },
    async submitForm() {
      // Prevent default form submission if triggered by form event
      if (event) event.preventDefault();
      
      // Basic validation
      if (!this.userId) {
        Swal.fire({
          icon: 'warning',
          title: 'Login Required',
          text: 'Please log in first to create an artist profile',
          confirmButtonColor: '#BF3131'
        });
        return;
      }
      
      if (!this.form.name) {
        Swal.fire({
          icon: 'warning',
          title: 'Name Required',
          text: 'Please enter the artist name.',
          confirmButtonColor: '#BF3131'
        });
        return;
      }

      if (!this.form.description) {
        Swal.fire({
          icon: 'warning',
          title: 'Description Required',
          text: 'Please provide a description.',
          confirmButtonColor: '#BF3131'
        });
        return;
      }
      
      if (!this.form.image) {
        Swal.fire({
          icon: 'warning',
          title: 'Image Required',
          text: 'Please select a profile image.',
          confirmButtonColor: '#BF3131'
        });
        return;
      }

      try {
        const payload = new FormData();
        payload.append("user_id", this.userId);
        payload.append("name", this.form.name);
        payload.append("profession", this.form.profession);

        payload.append("description", this.form.description || "");
        payload.append("achievements", this.form.achievements || "");
        payload.append("dob", this.form.dob || "");
        payload.append("quote", this.form.quote || "");
        if (this.form.image) payload.append("profile_image", this.form.image);
        this.galleryImages.forEach((img) => payload.append(`gallery_images`, img));
        
        // Filter out empty links before sending
        const validLinks = this.links.filter(link => link.link_type && link.link_url);
        payload.append("links", JSON.stringify(validLinks));
        console.log("📎 Links being sent:", validLinks);

        // Use env variable directly (it already includes /api)
        const baseUrl = process.env.VUE_APP_API_BASE_URL || 'https://sanskritirdapoon.in/api';
        
        const finalUrl = `${baseUrl}/artists`;
        console.log("🚀 Sending request to:", finalUrl);

        const res = await axios.post(finalUrl, payload, {
          headers: { 
            "Content-Type": "multipart/form-data" 
          },
        });

        console.log("✅ Artist Created:", res.data);
        Swal.fire({
          icon: 'success',
          title: 'Profile Created',
          text: 'Artist profile created successfully!',
          confirmButtonColor: '#BF3131'
        });
        
        // Emit event to parent component
        this.$emit('created');
        
        // Reset form
        this.form = {
          name: "",
          profession: "",
          description: "",
          achievements: "",
          dob: "",
          quote: "",
          image: null,
        };
        this.galleryImages = [];
        this.links = [{ link_type: "", link_url: "" }];
      } catch (err) {
        console.error("❌ Form submission error:", err);
        
        let errorMessage = err.response?.data?.error || err.message || "Failed to create artist profile. Please try again.";
        
        if (err.response?.status === 409) {
          errorMessage = "You have already created an artist profile. Each user can only create one artist profile.";
          this.$emit('already-exists');
        }
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonColor: '#BF3131'
        });
      }
    },
  },
};
</script>

<style scoped>
.input-field {
  @apply w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#BF3131] focus:border-transparent transition duration-200 bg-white;
}

/* Animation for adding links */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}
</style>
